import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { motion, useInView } from 'framer-motion';

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [sent, setSent] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const onSubmit = async (data) => {
    const subject = encodeURIComponent(data.subject || `Website Enquiry — ${data.name}`);
    const body = encodeURIComponent(
      `Name: ${data.name}\nEmail: ${data.email}${data.phone ? `\nPhone: ${data.phone}` : ''}\n\nMessage:\n${data.message}`
    );
    const mailtoHref = `mailto:support@aarohanstudio.in?subject=${subject}&body=${body}`;
    const link = document.createElement('a');
    link.href = mailtoHref;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSent(true);
    reset();
    setTimeout(() => setSent(false), 6000);
  };

  const inputStyle = {
    width: '100%', border: '1px solid #DDD0B8',
    background: '#F2EAD8', padding: '13px 16px', borderRadius: 2,
    fontFamily: "'DM Sans',sans-serif", fontSize: 14,
    color: '#1A1209', outline: 'none', transition: 'border 0.2s',
  };

  return (
    <section id="contact" style={{ background: '#F2EAD8', padding: '96px 6%', position: 'relative', overflow: 'hidden' }}>

      {/* Musical Staff Background */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
        <style>{`
          @keyframes note-float {
            0%   { transform: translateX(-60px); opacity: 0; }
            8%   { opacity: 0.28; }
            88%  { opacity: 0.22; }
            100% { transform: translateX(calc(100vw + 60px)); opacity: 0; }
          }
          .staff-note {
            position: absolute;
            color: #C8922A;
            font-size: 22px;
            animation: note-float linear infinite;
            will-change: transform, opacity;
          }
        `}</style>

        {/* Floating music notes */}
        {[
          { note: '♩', top: '8%', dur: '18s', delay: '0s' },
          { note: '♪', top: '15%', dur: '22s', delay: '3s' },
          { note: '♫', top: '20%', dur: '16s', delay: '7s' },
          { note: '♩', top: '26%', dur: '20s', delay: '1.5s' },
          { note: '♬', top: '32%', dur: '25s', delay: '10s' },
          { note: '♪', top: '38%', dur: '17s', delay: '5s' },
          { note: '♩', top: '44%', dur: '21s', delay: '13s' },
          { note: '♫', top: '50%', dur: '19s', delay: '8s' },
          { note: '♬', top: '56%', dur: '24s', delay: '16s' },
          { note: '♪', top: '62%', dur: '15s', delay: '4s' },
          { note: '♩', top: '68%', dur: '23s', delay: '11s' },
          { note: '♫', top: '74%', dur: '20s', delay: '2s' },
          { note: '♬', top: '80%', dur: '18s', delay: '6s' },
          { note: '♩', top: '86%', dur: '22s', delay: '9s' },
          { note: '♪', top: '12%', dur: '26s', delay: '14s' },
          { note: '♫', top: '22%', dur: '14s', delay: '17s' },
          { note: '♬', top: '35%', dur: '28s', delay: '0.5s' },
          { note: '♩', top: '48%', dur: '13s', delay: '12s' },
          { note: '♪', top: '58%', dur: '27s', delay: '3.5s' },
          { note: '♫', top: '72%', dur: '15s', delay: '18s' },
          { note: '♩', top: '88%', dur: '24s', delay: '7.5s' },
          { note: '♬', top: '5%', dur: '20s', delay: '20s' },
          { note: '♪', top: '42%', dur: '16s', delay: '22s' },
          { note: '♩', top: '65%', dur: '19s', delay: '15s' },
          { note: '♫', top: '30%', dur: '23s', delay: '19s' },
          { note: '♬', top: '55%', dur: '17s', delay: '24s' },
          { note: '♪', top: '78%', dur: '21s', delay: '11.5s' },
          { note: '♩', top: '18%', dur: '29s', delay: '26s' },
        ].map(({ note, top, dur, delay }, i) => (
          <span key={i} className="staff-note" style={{
            top,
            animationDuration: dur,
            animationDelay: delay,
          }}>{note}</span>
        ))}
      </div>
      <span className="ghost-text" style={{ bottom: '-8%', right: '-3%', fontSize: 'clamp(80px,12vw,160px)', opacity: 0.05, zIndex: 0 }}>संपर्क</span>

      <div className="max-w" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <span className="eyebrow">Connect</span>
          <h2 className="section-title">Let's Make Something<br /><em style={{ fontStyle: 'italic', color: '#C8922A' }}>Beautiful</em></h2>
          <div className="section-rule" />
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 32 : 64, marginTop: 48 }}>
          <div>
            <p style={{ fontSize: 15, lineHeight: 1.85, color: '#7A6E5E', fontWeight: 300, marginBottom: 32, fontFamily: "'DM Sans',sans-serif" }}>
              Whether you're booking a session, enquiring about classes, or just want to see the studio — we'd love to hear from you. Walk-ins welcome Tuesday to Sunday.
            </p>
            {[
              { icon: '📍', text: '3, Palhar Nagar, Indore, Madhya Pradesh, India — 452005', href: null },
              { icon: '✉️', text: 'support@aarohanstudio.in', href: 'mailto:support@aarohanstudio.in' },
              { icon: '📞', text: '+91 7313195517', href: 'tel:+917313195517' },
            ].map(({ icon, text, href }) => (
              <div key={text} style={{ display: 'flex', gap: 14, marginBottom: 18, fontSize: 14, color: '#1A1209', fontFamily: "'DM Sans',sans-serif" }}>
                <span>{icon}</span>
                {href ? (
                  <a href={href} style={{ color: '#1A1209', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = '#C8922A'}
                    onMouseLeave={e => e.target.style.color = '#1A1209'}
                  >{text}</a>
                ) : (
                  <span>{text}</span>
                )}
              </div>
            ))}
            <div style={{ marginTop: 32, background: '#E8DCC8', borderRadius: 4, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #DDD0B8' }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 24, marginBottom: 8 }}>📍</p>
                <p style={{ fontSize: 13, color: '#7A6E5E', fontFamily: "'DM Sans',sans-serif" }}>Aarohan Studio, Indore</p>
                <a href="https://maps.app.goo.gl/9WH9yyzCKf5ZyCV97" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#C8922A', marginTop: 6, display: 'block', fontFamily: "'DM Sans',sans-serif" }}>Open in Google Maps →</a>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <input {...register('name', { required: true })} placeholder="Your Name" id="contact-name"
                  style={{ ...inputStyle, borderColor: errors.name ? '#D85A30' : '#DDD0B8' }}
                  onFocus={e => e.target.style.borderColor = '#C8922A'}
                  onBlur={e => e.target.style.borderColor = errors.name ? '#D85A30' : '#DDD0B8'} />
                {errors.name && <p style={{ fontSize: 11, color: '#D85A30', marginTop: 4, fontFamily: "'DM Sans',sans-serif" }}>Required</p>}
              </div>
              <div>
                <input {...register('email', { required: true, pattern: /^\S+@\S+$/i })} placeholder="Email Address" id="contact-email"
                  style={{ ...inputStyle, borderColor: errors.email ? '#D85A30' : '#DDD0B8' }}
                  onFocus={e => e.target.style.borderColor = '#C8922A'}
                  onBlur={e => e.target.style.borderColor = errors.email ? '#D85A30' : '#DDD0B8'} />
                {errors.email && <p style={{ fontSize: 11, color: '#D85A30', marginTop: 4, fontFamily: "'DM Sans',sans-serif" }}>Valid email required</p>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input {...register('phone')} placeholder="Phone (optional)" id="contact-phone" type="tel" style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#C8922A'}
                onBlur={e => e.target.style.borderColor = '#DDD0B8'} />
              <input {...register('subject')} placeholder="Subject" id="contact-subject" style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#C8922A'}
                onBlur={e => e.target.style.borderColor = '#DDD0B8'} />
            </div>
            <textarea {...register('message', { required: true })} rows={5} id="contact-message" placeholder="Describe your enquiry — recording, mixing, mastering, or music classes. Include any relevant details."
              style={{ ...inputStyle, resize: 'vertical', borderColor: errors.message ? '#D85A30' : '#DDD0B8' }}
              onFocus={e => e.target.style.borderColor = '#C8922A'}
              onBlur={e => e.target.style.borderColor = errors.message ? '#D85A30' : '#DDD0B8'} />
            {errors.message && <p style={{ fontSize: 11, color: '#D85A30', marginTop: -8, fontFamily: "'DM Sans',sans-serif" }}>Required</p>}
            <button type="submit" disabled={isSubmitting} className="btn-liquid" data-cursor="SEND" style={{ border: 'none' }}>
              {isSubmitting ? 'Opening mail client...' : sent ? '✓ Email client opened!' : 'Send Message →'}
            </button>
            {sent && (
              <p style={{ fontSize: 12, color: '#7A6E5E', textAlign: 'center', fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
                Your message has been composed in your email client. Hit Send to reach us, or write to{' '}
                <a href="mailto:support@aarohanstudio.in" style={{ color: '#C8922A' }}>support@aarohanstudio.in</a> directly.
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
